import { ArrowRight, MapPin, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HERO_GRAPHIC, LOGO_URL, STATS } from "@/components/landing/content";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Background grid + glows */}
      <div aria-hidden className="absolute inset-0 bg-grid opacity-60" />
      <div
        aria-hidden
        className="glow-blue"
        style={{ top: "-120px", left: "-160px" }}
      />
      <div
        aria-hidden
        className="glow-orange"
        style={{ top: "100px", right: "-140px" }}
      />

      <div className="relative mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7 fade-up">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3 py-1.5 text-xs font-semibold tracking-wide text-slate-700"
          >
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-orange-500" />
            <MapPin size={12} className="text-blue-700" />
            <span>Based in Lexington, Kentucky</span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display mt-5 text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 leading-[1.02]"
          >
            Unlock your brand’s{" "}
            <span className="brand-text-gradient">full potential.</span>
          </h1>

          <p
            data-testid="hero-subheadline"
            className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl font-body"
          >
            JT Brand Scales is a Kentucky-based marketing agency. We help local
            businesses build sharper brands, smarter marketing and the kind of
            online presence that turns attention into real customers.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              asChild
              size="lg"
              data-testid="hero-cta-primary"
              className="rounded-full bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 h-12 btn-shine"
            >
              <a href="#contact">
                Get a free quote <ArrowRight size={18} className="ml-1" />
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              data-testid="hero-cta-secondary"
              className="rounded-full border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-900 font-semibold px-6 h-12"
            >
              <a href="#pricing">See pricing</a>
            </Button>

            <div className="ml-1 flex items-center gap-2 text-sm text-slate-600">
              <div className="flex -space-x-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    size={16}
                    className="fill-orange-500 text-orange-500"
                  />
                ))}
              </div>
              <span className="font-medium">Loved by KY business owners</span>
            </div>
          </div>

          {/* Stats */}
          <dl
            data-testid="hero-stats"
            className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6 max-w-2xl"
          >
            {STATS.map((s) => (
              <div key={s.label} className="border-l-2 border-orange-500 pl-3">
                <dt className="font-display text-3xl md:text-4xl font-extrabold text-slate-900">
                  {s.value}
                </dt>
                <dd className="mt-1 text-xs md:text-sm text-slate-600 font-medium">
                  {s.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Visual */}
        <div className="lg:col-span-5 fade-up delay-2">
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-blue-100 via-white to-orange-100 -z-10" />
            <div className="relative rounded-[2rem] border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(15,23,42,0.25)] overflow-hidden">
              <img
                src={HERO_GRAPHIC}
                alt="Brand growth illustration"
                className="w-full h-full object-cover"
                data-testid="hero-graphic"
              />
            </div>

            {/* Floating chip */}
            <div className="hidden md:flex absolute -bottom-6 -left-6 items-center gap-3 rounded-2xl bg-white border border-slate-200 shadow-lg px-4 py-3">
              <img
                src={LOGO_URL}
                alt="JT"
                className="h-10 w-10 rounded-lg object-cover"
              />
              <div className="leading-tight">
                <div className="text-sm font-display font-extrabold text-slate-900">
                  +312% leads
                </div>
                <div className="text-xs text-slate-500">90-day growth case</div>
              </div>
            </div>

            <div className="hidden md:flex absolute -top-5 -right-5 items-center gap-2 rounded-full bg-blue-700 text-white px-4 py-2 shadow-lg">
              <span className="inline-flex h-2 w-2 rounded-full bg-orange-400 animate-pulse" />
              <span className="text-xs font-semibold tracking-wide">
                Now booking December
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
